import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { SearchGroupContext } from "./context";
import { useQuery, useSearchGroup } from "./hooks";
import type { SourceObject } from "./hooks/useQuery/dto";
import type { SearchGroupProviderProps, SearchProviderRef } from "./dto";
import SearchItem from "./item";
import { removeNullProperties } from "./utils";

type SearchProvider = React.ForwardRefExoticComponent<
  SearchGroupProviderProps<SourceObject> &
    React.RefAttributes<SearchProviderRef<SourceObject>>
> & {
  Item: typeof SearchItem;
  useSearchGroup: typeof useSearchGroup;
};

/**
 * 搜索控件包装器上下文组件
 */
const SearchGroupProvider = forwardRef(
  <T extends SourceObject = any>(
    props: SearchGroupProviderProps<T>,
    ref: ForwardedRef<SearchProviderRef<T>>
  ) => {
    const { initValue, options, onSubmit } = props;
    const [allowReset, setAllowReset] = useState(true);
    const [query, dispatch] = useQuery<T>(initValue, {
      ...options,
      allowReset,
      setAllowReset,
    });
    const queryRef = useRef(query);
    const submitRef = useRef<NodeJS.Timeout>(null);

    const submit = () => {
      if (submitRef.current) clearTimeout(submitRef.current);
      submitRef.current = setTimeout(() => {
        /** 删除所有值为null的属性 */
        const cleanQuery = removeNullProperties(queryRef.current);

        onSubmit?.(cleanQuery);
        setAllowReset(true);
      }, 0);
    };

    const refresh = () => {
      dispatch();
      submit();
    };

    const reset = () => {
      dispatch("RESET");
    };

    useImperativeHandle(
      ref,
      () => {
        queryRef.current = query;
        return {
          getCurrentQuery: (filterNull = true) => {
            if (filterNull) return removeNullProperties(query);
            return query;
          },
          submit: () => submit(),
          dispatch,
          refresh,
          reset,
        };
      },
      [query, dispatch, submit, refresh, reset]
    );

    useEffect(() => {
      return () => {
        if (submitRef.current) clearTimeout(submitRef.current);
      };
    }, []);

    const innerValue = useMemo(
      () => ({ query, dispatch, refresh, submit, reset }),
      [query, dispatch, refresh, submit, reset]
    );

    return (
      <SearchGroupContext.Provider value={innerValue}>
        {props.children}
      </SearchGroupContext.Provider>
    );
  }
) as SearchProvider;

export default SearchGroupProvider;

SearchGroupProvider.Item = SearchItem;
SearchGroupProvider.useSearchGroup = useSearchGroup;
